import { ParsedSignature, parseSignature } from "./signature";
import { chatSenders, emailSubject, hasEmailHeader, stripMessage } from "./stripMessage";
import { arabicCompanyToEnglish, arabicNameToEnglish, arabicPlaceToEnglish, arabicTitleToEnglish, hasArabic, westernDigits } from "./arabicText";

/** Company details beyond a signature's name, phones, emails and website. */
export interface CompanyExtras {
    /** "Trade name" on a licence — the company's name to use. */
    tradeName: string | null;
    legalName: string | null;
    street: string | null;
    poBox: string | null;
    city: string | null;
    country: string | null;
    licenceNumber: string | null;
    licenceExpires: string | null;
    taxNumber: string | null;
    activities: string | null;
    /** The message itself — greeting, signature and quoted replies off — when it says what they offer or need. */
    message: string | null;
    /** Their side, from the words: "we run / our rates" → provider (they sell to us); "we need / please quote" → client. */
    side: "provider" | "client" | null;
    /** How it reached us: an email (its header was pasted) or a WhatsApp chat. */
    via: "email" | "whatsapp" | null;
    subject: string | null;
    /** The person's name as written in Arabic, when it was (the English spelling is in `person`, to check). */
    personArabic?: string | null;
    /** The company's name as written in Arabic, when it was (the English is in `company`, to check). */
    companyArabic?: string | null;
}

/** Everything "Fill with AI" can put into a record: a signature's fields plus company details. */
export type ReadDetails = ParsedSignature & CompanyExtras;

export const NO_EXTRAS: CompanyExtras = {
    tradeName: null, legalName: null, street: null, poBox: null, city: null, country: null, licenceNumber: null, licenceExpires: null, taxNumber: null, activities: null,
    message: null, side: null, via: null, subject: null,
};

const TAX = /\b(?:TRN|VAT\s*(?:Reg(?:istration)?\.?\s*)?(?:No\.?|Number|#)?|Tax\s*(?:Registration\s*)?(?:No\.?|Number|ID|#))\s*[:#.]?\s*([A-Z]{0,3}\s?\d[\d\s-]{6,20}\d)/i;
const LICENCE = /\b(?:Trade\s*Licen[cs]e|Licen[cs]e|Commercial\s*Reg(?:istration)?|C\.R\.|CR)\s*(?:No\.?|Number|#)?\s*[:#.]?\s*([A-Z]{0,4}[-/]?\d[A-Z0-9/-]{2,20})/i;
const PO_BOX = /\bP\.?\s?O\.?\s*Box\s*[:#.]?\s*(\d{2,8})/i;
const EXPIRY = /\b(?:Expiry|Expires|Valid\s*(?:until|till|to)|Expiry\s*Date)\s*(?:Date)?\s*[:.]?\s*(\d{1,2}[/.-]\d{1,2}[/.-]\d{4}|\d{4}-\d{2}-\d{2})/i;
const STREET_WORDS = /\b(office|suite|floor|flr|building|bldg|tower|street|st\.|road|rd\.|avenue|ave\.?|area|plot|block|district|centre|center|business bay|free zone|freezone|al\s+\w+\s+(?:st|street|road))\b/i;
const ADDRESS_LABEL = /^\s*(?:address|addr\.?|location|head office|registered office)\b\s*[:-]?\s*/i;
const ACTIVITY_LABEL = /^\s*(?:activities|activity|licensed activities|business activity)\s*[:-]\s*(.+)$/i;
const LEGAL_LABEL = /^\s*(?:legal name|company name|registered name)\s*[:-]?\s+(.+)$/i;
const TRADE_LABEL = /^\s*(?:trade name|business name)\s*[:-]?\s+(.+)$/i;

const ALIASES: Record<string, string> = {
    uae: "United Arab Emirates", "u.a.e": "United Arab Emirates", "u.a.e.": "United Arab Emirates", emirates: "United Arab Emirates",
    ksa: "Saudi Arabia", "k.s.a": "Saudi Arabia", "saudi": "Saudi Arabia", uk: "United Kingdom", "u.k.": "United Kingdom", usa: "United States", "u.s.a.": "United States",
    palestine: "Palestine", jordan: "Jordan", qatar: "Qatar", oman: "Oman", bahrain: "Bahrain", kuwait: "Kuwait", egypt: "Egypt", iraq: "Iraq", lebanon: "Lebanon",
};

let countryNames: string[] | null = null;
/** English names of every country the browser knows (no list to maintain). */
const allCountries = (): string[] => {
    if (countryNames) return countryNames;
    const names = new Set<string>(Object.values(ALIASES));
    try {
        const display = new (Intl as any).DisplayNames(["en"], { type: "region" });
        for (let a = 65; a <= 90; a++) {
            for (let b = 65; b <= 90; b++) {
                const code = String.fromCharCode(a, b);
                const name = display.of(code);
                if (name && name !== code && !/unknown/i.test(name)) names.add(name);
            }
        }
    } catch {
        // older browsers: the aliases above still work
    }
    countryNames = Array.from(names).sort((x, y) => y.length - x.length);
    return countryNames;
};

const escape = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** The country named in a line ("Dubai, UAE" → United Arab Emirates) and where it sits. */
const countryIn = (line: string): { name: string; at: number } | null => {
    const lower = line.toLowerCase();
    for (const name of allCountries()) {
        const match = new RegExp(`\\b${escape(name)}\\b`, "i").exec(line);
        if (match) return { name, at: match.index };
    }
    for (const [alias, name] of Object.entries(ALIASES)) {
        const match = new RegExp(`(^|[\\s,(-])${escape(alias)}($|[\\s,).-])`, "i").exec(lower);
        if (match) return { name, at: match.index + match[1].length };
    }
    return null;
};

/**
 * Every country a text names ("now we cover Qatar", "Riyadh, KSA"), first
 * named first. Full names count when capitalised (so a "Chad" or a "Jersey"
 * in a sentence isn't taken lightly); the common abbreviations count any case.
 * @param text - A price list's lines, heading and intro.
 */
export const countriesIn = (text: string): string[] => {
    const found = new Map<string, number>();
    const add = (name: string, at: number) => { if (!found.has(name) || (found.get(name) as number) > at) found.set(name, at); };
    for (const name of allCountries()) {
        const match = new RegExp(`(?<![\\p{L}])${escape(name)}(?![\\p{L}])`, "u").exec(text);
        if (match) add(name, match.index);
    }
    const lower = text.toLowerCase();
    for (const [alias, name] of Object.entries(ALIASES)) {
        const match = new RegExp(`(^|[\\s,(-])${escape(alias)}($|[\\s,).:;-])`).exec(lower);
        if (match) add(name, match.index + match[1].length);
    }
    return Array.from(found.entries()).sort((a, b) => a[1] - b[1]).map(([name]) => name);
};

const isoDate = (raw: string): string | null => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const parts = raw.split(/[/.-]/).map(Number);
    if (parts.length !== 3) return null;
    const [day, month, year] = parts;
    if (!(day >= 1 && day <= 31 && month >= 1 && month <= 12 && year > 1900)) return null;
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

/**
 * Company details read BY CODE from text (a signature, letterhead, company
 * profile or licence text): legal name, street, P.O. Box, city, country,
 * licence and expiry, tax / VAT / TRN, activities. Nothing is sent anywhere.
 * @param text - The text.
 */
export const parseCompanyDetails = (text: string): CompanyExtras => {
    const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    const out: CompanyExtras = { ...NO_EXTRAS };
    const tax = TAX.exec(text);
    if (tax) out.taxNumber = tax[1].replace(/\s+/g, "");
    const licence = LICENCE.exec(text);
    if (licence && licence[1] !== out.taxNumber) out.licenceNumber = licence[1];
    const box = PO_BOX.exec(text);
    if (box) out.poBox = `P.O. Box ${box[1]}`;
    const expiry = EXPIRY.exec(text);
    if (expiry) out.licenceExpires = isoDate(expiry[1]);
    for (const line of lines) {
        const legal = LEGAL_LABEL.exec(line);
        if (legal && !out.legalName) out.legalName = legal[1].trim().slice(0, 200);
        const trade = TRADE_LABEL.exec(line);
        if (trade && !out.tradeName) out.tradeName = trade[1].trim().slice(0, 150);
        const activity = ACTIVITY_LABEL.exec(line);
        if (activity && !out.activities) out.activities = activity[1].trim().slice(0, 300);
        if (!out.country) {
            const country = countryIn(line);
            if (country) {
                out.country = country.name;
                const before = line.slice(0, country.at).replace(ADDRESS_LABEL, "").replace(PO_BOX, "").split(/[,–-]/).map((part) => part.trim()).filter(Boolean);
                // OCR noise in front ("Za Dubai"); real prefixes stay ("Al Ain", "La Paz", "St Gallen")
                const city = before[before.length - 1]?.replace(/^(?!(?:[Aa][lsdrntz]|[Ee]l|[Ll][aeo]|[Dd][aeiu]|[Ss]t|[Hh]o)\s)\p{L}{1,2}\s+(?=\p{Lu}\p{L}{2,})/u, "");
                if (city && /^[\p{L}\s.'-]{2,40}$/u.test(city) && !STREET_WORDS.test(city)) out.city = city;
            }
        }
        const body = line.replace(ADDRESS_LABEL, "").replace(PO_BOX, "");
        const isSentence = /:\s*$/.test(line) || body.split(/\s+/).filter(Boolean).length > 10 || /\b(please|note|our|we|you|your|kindly|regards)\b/i.test(body);
        if (!out.street && !isSentence && (ADDRESS_LABEL.test(line) || STREET_WORDS.test(line)) && !/@|https?:|www\./i.test(line) && !/\d{7,}/.test(line.replace(/\s/g, ""))) {
            const street = line.replace(ADDRESS_LABEL, "").replace(PO_BOX, "").replace(/[,\s]+$/, "").trim();
            if (street.length >= 4) out.street = street.slice(0, 200);
        }
    }
    return out;
};

// "we run / our rates / price list" (they sell to us) vs "we need / please quote" (they buy from us)
const OFFER_WORDS = /\b(?:we (?:run|offer|provide|supply|operate|rent|deliver|cover|specialise|specialize)|our (?:services|fleet|rates|prices|price list|team|vehicles|guards|officers)|updated (?:\w+ )?rates|(?:day|daily|hourly) rates?|rates? (?:start|from)|price list|fleet (?:register|list|available)|happy to (?:send|share|quote|support))\b|نقدم|نوفر|لدينا|أسعارنا|خدماتنا|أسطول/i;
const NEED_WORDS = /\b(?:we need|we require|we(?:'re| are) looking for|could you (?:please )?(?:quote|send|provide|arrange)|can you (?:please )?(?:quote|provide|arrange)|please (?:quote|send|arrange)|request(?:ing)? a quote|send (?:us )?(?:a|your) quote|quote for)\b|نحتاج|نطلب|أرجو إرسال|نرجو|عرض السعر/i;
const GREETING = /^(?:subject\s*:.*|(?:hello|hi|hey|dear|good (?:morning|afternoon|evening)|greetings)\b[^.!?]{0,60}|(?:السلام عليكم|مرحبا|مرحباً|صباح الخير|مساء الخير)[^.!؟]{0,40})$/i;

/**
 * What a pasted message says, read by code: the body (for "What they offer &
 * notes"), their side, how it came and its subject.
 * @param text - What was pasted.
 */
export const messageFacts = (text: string): Pick<CompanyExtras, "message" | "side" | "via" | "subject"> => {
    const via = chatSenders(text).length ? "whatsapp" : hasEmailHeader(text) ? "email" : null;
    const body = stripMessage(text, 4000).split("\n").filter((line) => !GREETING.test(line.trim())).join("\n").replace(/\n{3,}/g, "\n\n").trim();
    const offers = OFFER_WORDS.test(body);
    const needs = NEED_WORDS.test(body);
    return {
        message: body && (offers || needs) ? body.slice(0, 1500) : null,
        side: offers && !needs ? "provider" : needs && !offers ? "client" : null,
        via,
        subject: emailSubject(text),
    };
};

/** A signature's fields plus company details, from one text. */
export const readDetails = (pasted: string): ReadDetails => {
    // Arabic-Indic digits as 0–9 first, so ٠٥٠… phones are found like any other
    const text = westernDigits(pasted);
    const signature = parseSignature(text);
    const extras = parseCompanyDetails(text);
    return inEnglish({ ...signature, ...extras, ...messageFacts(text), company: extras.tradeName ?? signature.company }, text);
};

/**
 * CRX keeps records in English (user rule, 2026-10-15): a name, company, job
 * title or place read in Arabic is put in English by code — the names never
 * go to the AI — and the Arabic original is kept beside it (for the notes and
 * a "from Arabic, check spelling" hint).
 * @param read - What was read.
 */
const inEnglish = (read: ReadDetails, text = ""): ReadDetails => {
    const out: ReadDetails = { ...read, personArabic: null, companyArabic: null };
    // a bilingual card or signature prints the name both ways on one line ("Sara Al Mansouri سارة المنصوري"): keep the Arabic
    if (read.person && !hasArabic(read.person)) {
        const line = text.split(/\r?\n/).find((candidate) => candidate.includes(read.person!) && hasArabic(candidate));
        const arabic = line ? line.replace(read.person, "").replace(/[\u200E\u200F\u202A-\u202E\u2066-\u2069|•·,–-]/g, " ").replace(/\s+/g, " ").trim() : "";
        if (arabic && hasArabic(arabic) && arabic.split(" ").length <= 5 && !/[A-Za-z\d@]/.test(arabic)) out.personArabic = arabic;
    }
    if (read.person && hasArabic(read.person)) {
        out.personArabic = read.person;
        out.person = arabicNameToEnglish(read.person).english || read.person;
    }
    if (read.company && hasArabic(read.company)) {
        out.companyArabic = read.company;
        out.company = arabicCompanyToEnglish(read.company).english || read.company;
    }
    if (read.legalName && hasArabic(read.legalName)) out.legalName = arabicCompanyToEnglish(read.legalName).english || read.legalName;
    if (read.tradeName && hasArabic(read.tradeName)) out.tradeName = out.company;
    if (read.title && hasArabic(read.title)) out.title = arabicTitleToEnglish(read.title) ?? read.title;
    if (read.city && hasArabic(read.city)) out.city = arabicPlaceToEnglish(read.city, "city") ?? read.city;
    if (read.country && hasArabic(read.country)) out.country = arabicPlaceToEnglish(read.country, "country") ?? read.country;
    return out;
};

/** How many company-level fields a read found (to judge whether a document read well). */
export const companyFieldCount = (details: ReadDetails): number =>
    [details.company, details.website, details.emails[0], details.phones[0], details.street, details.poBox, details.city, details.country, details.licenceNumber, details.taxNumber]
        .filter(Boolean).length;

/**
 * The text of a Word file (.docx), read in the browser: the document's
 * paragraphs, one per line. Nothing is uploaded.
 * @param file - The .docx file.
 */
export const docxText = async (file: File): Promise<string> => {
    const JSZip = (await import("jszip")).default;
    const zip = await JSZip.loadAsync(await file.arrayBuffer());
    const xml = await zip.file("word/document.xml")?.async("string");
    if (!xml) return "";
    return xml
        .replace(/<w:tab\/>/g, " ")
        .replace(/<\/w:p>/g, "\n")
        .replace(/<w:br\/>/g, "\n")
        .replace(/<[^>]+>/g, "")
        .replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, "\"").replace(/&apos;/g, "'")
        .split("\n").map((line) => line.trim()).filter(Boolean).join("\n");
};
